import { Icon } from "@iconify/react";
import clsx from "clsx";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Card, Dropdown, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDoctorSearchList } from "../../api/CalendarAPI";
import { useAutoCompleteLocation } from "../../hooks/LocationAutoComplete";
import i18n from "../../i18n/i18n";
import { ignoreAccentPunc } from "../../utils/commonFunctions";
import { AppNav, AssociationTypes, Colors, DoctorTitle, Images } from "../../utils/constants";
import { useConsultationContext } from "./PatientMainContext/PatientMainContext";
import PlacesSearchBar from "../../utils/PlacesSearchBar";
import { useTimeHook } from "../../hooks/useTimeHook";
import { useDispatch } from "react-redux";
import { PatientAppointmentActions } from "../../redux/reducer/PatientAppointmentReducer";

const FindAppointment = forwardRef(({ }, ref) => {
    const doctorListAPI = useDoctorSearchList({ getInitial: false, resBody: '' })
    const consultationContext = useConsultationContext()
    const [searchText, setSearchText] = useState({ text: '', special: {}, click: false })
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [showList, setShowList] = useState(false)
    const [searchType, setSearchType] = useState(null)
    const { location, currentLocation } = useAutoCompleteLocation({ aroundMe: true })
    const [searchRes, setSearchRes] = useState([])
    const searchRef = useRef()

    const isEmpty = () => {
        if (searchText?.text.trim()) {
            return true
        } else {
            if (!(searchRef.current.className.includes('error'))) {
                searchRef.current.className = searchRef.current.className + ' error'
            }
            searchRef.current.focus()
        }
    }
    const onSubmit = () => {
        const docSearch = searchRes?.doctors?.[0]
        const fullname = (docSearch?.first_name?.toLowerCase?.().trim() || '') + ' ' + (docSearch?.last_name?.toLowerCase?.().trim() || '')
        const isDoctorSearched = docSearch?.first_name.toLowerCase().trim().includes(searchText?.text?.toLowerCase().trim())
            || docSearch?.last_name.toLowerCase().trim().includes(searchText?.text?.toLowerCase().trim()) || fullname?.includes(searchText?.text?.toLowerCase().trim())
        const firstItem = searchRes?.speciality?.[0]?.specialite_nom ? 'speciality' :
            searchRes?.tags?.[0]?.name ? 'tag' :
                searchRes?.categories?.[0]?.category_nom ? 'category' :
                    (isDoctorSearched && fullname) ? 'doctor' :
                        (searchRes?.locations?.[0]?.location_name) ? 'location' : null

        // if (!firstItem || !searchType?.type) return
        const type = searchType?.type || firstItem
        switch (type) {
            case 'speciality':
                const speciality = searchRes?.speciality?.[0]
                dispatch(PatientAppointmentActions.setPatientAppointment({
                    specialisation: searchType?.value || {
                        id: speciality?.id,
                        nom: speciality?.specialite_nom,
                        parent_speciality_id: speciality?.parent_speciality_id
                    },
                    isSpeciality: true,
                    step: 1,
                    location: location,
                    currentLocation: currentLocation,
                    ...(consultationContext.consultDetails?.isPatientDetails ? {
                        patient: consultationContext.consultDetails?.patient,
                        isPatientDetails: consultationContext.consultDetails?.isPatientDetails,
                        isAgendaSelected: consultationContext.consultDetails?.isAgendaSelected
                    } : {}),
                }))
                navigate(AppNav.patientAppointment)
                break;
            case 'category':
                const category = searchRes?.categories?.[0]
                dispatch(PatientAppointmentActions.setPatientAppointment({
                    specialisation: searchType?.value?.speciality || {
                        id: category?.specialite_id, nom: category?.specialite_nom
                    },
                    category: searchType?.value?.category || { id: category?.id, nom: category?.category_nom },
                    isCategory: true,
                    step: 1,
                    location: location,
                    currentLocation: currentLocation,
                    ...(consultationContext.consultDetails?.isPatientDetails ? {
                        patient: consultationContext.consultDetails?.patient,
                        isPatientDetails: consultationContext.consultDetails?.isPatientDetails,
                        isAgendaSelected: consultationContext.consultDetails?.isAgendaSelected
                    } : {}),
                }))
                navigate(AppNav.patientAppointment)
                break;
            case 'tag':
                const tag = searchRes?.tags?.[0]
                dispatch(PatientAppointmentActions.setPatientAppointment({
                    specialisation: searchType?.value || {
                        id: tag?.speciality?.id,
                        nom: tag?.speciality?.specialite_nom,
                        parent_speciality_id: tag?.speciality?.parent_speciality_id
                    },
                    isSpeciality: true,
                    step: 1,
                    location: location,
                    currentLocation: currentLocation,
                    ...(consultationContext.consultDetails?.isPatientDetails ? {
                        patient: consultationContext.consultDetails?.patient,
                        isPatientDetails: consultationContext.consultDetails?.isPatientDetails,
                        isAgendaSelected: consultationContext.consultDetails?.isAgendaSelected,
                    } : {}),
                }))
                navigate(AppNav.patientAppointment)
                break;
            case 'doctor':
                navigate(AppNav.doctorInfo, {
                    state: {
                        ...searchRes?.doctors?.[0], isDoctor: true
                    }
                })
                break;
            case 'location':
                navigate(AppNav.doctorInfo, {
                    state: {
                        ...searchRes?.locations?.[0], isDoctor: false
                    }
                })
                break;
            default:
                break;
        }


    }

    const timeOut = useTimeHook({ duration: 400 })
    const debouncedcall = (searchKey) => {
        timeOut.delay(
            () => {
                doctorListAPI.get(`?search=${searchKey}`)
            }
        )
    }


    useEffect(() => {
        if (searchText?.text?.ignoreAccent?.()?.toLowerCase?.() === doctorListAPI.data?.search?.ignoreAccent?.()?.toLowerCase?.()) {
            // const tags = doctorListAPI.data?.speciality?.flatMap(special => special?.tag?.map(tag => ({ id: special?.id, name: tag })))
            const tags = doctorListAPI.data?.speciality_tag?.reduce((pre, curr) => {
                curr?.tag?.forEach?.((data) => {
                    if (pre?.find?.(obj => obj?.name.toLowerCase() == data?.toLowerCase?.())) {
                        return pre
                    }
                    pre.push(
                        {
                            id: curr?.id,
                            name: data,
                            speciality: { id: curr?.id, specialite_nom: curr?.specialite_nom }
                        }
                    )
                })
                return pre
            }, [])
            doctorListAPI.data.tags = tags
            setSearchRes(doctorListAPI.data)
        }
    }, [doctorListAPI.data])

    return (
        <div className="banner-block">
            {!consultationContext.consultDetails?.isPatientDetails && (
                <div className="hero-heading">
                    <h1>{i18n.t('hero_title')}</h1>
                    <p>{i18n.t('hero_subtitle')}</p>
                </div>
            )}
            <Card className="nw-card">
                <p className="title pati-title">{i18n.t('make_Appointment')}</p>
                <div className="hero-search">
                    <Form.Group className="form-group search-box d-flex">
                        <Dropdown
                            className="selected-item">
                            <Dropdown.Toggle
                                className="search-doctor">
                                <Icon icon="iconoir:search" />
                                <Form.Control
                                    ref={searchRef}
                                    type="text"
                                    className="form-control"
                                    onFocus={() => {
                                        setShowList(true)
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => {
                                            setShowList(false)
                                        }, 200)
                                    }}
                                    placeholder={i18n.t('search_place')}
                                    value={searchText?.text?.capitalizeEachWord?.()}
                                    onChange={(event) => {
                                        const value = event.target.value
                                        // setSearched({})
                                        setSearchType(null)
                                        setSearchText({ text: value, click: false })
                                        setShowList(true)
                                        if (value.length > 0) {
                                            if (searchRef.current.className.includes('error')) {
                                                searchRef.current.className = searchRef.current.className.replace('error', '')
                                            }
                                            debouncedcall(value)
                                        }
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === ' ') {
                                            setTimeout(() => {
                                                setShowList(true)
                                            }, 500)
                                        }
                                    }}
                                />
                            </Dropdown.Toggle>
                            <Dropdown.Menu
                                show={showList}
                                className={clsx({ 'search-drop doctor-list': true, 'show': showList })}>
                                {searchText?.text?.length > 0 &&
                                    <>
                                        {Array.isArray(searchRes?.speciality) && searchRes?.speciality?.map((speciality, index) => {
                                            const specialite_nom = speciality?.specialite_nom
                                            return (
                                                specialite_nom && <Dropdown.Item
                                                    onClick={() => {
                                                        setSearchText({ text: specialite_nom, special: speciality, click: true })  // Previous version
                                                        doctorListAPI.get(`?search=${specialite_nom}`)
                                                        setSearchType({
                                                            type: 'speciality', value: {
                                                                id: speciality?.id,
                                                                nom: speciality?.specialite_nom,
                                                                parent_speciality_id: speciality?.parent_speciality_id
                                                            }
                                                        })
                                                    }}
                                                    key={index}
                                                    className="doctor-list-item">
                                                    <div className="doc-detail" style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        flex: 1
                                                    }}>
                                                        <div className="doc-top">
                                                            <HighlightedText actualValue={specialite_nom?.capitalizeEachWord()} searchText={searchText?.text?.trim()} />
                                                        </div>
                                                    </div>
                                                </Dropdown.Item>
                                            )
                                        })}
                                        {Array.isArray(searchRes?.tags) && searchRes?.tags?.map((tag, index) => {
                                            return (
                                                <Dropdown.Item
                                                    onClick={() => {
                                                        setSearchText({ text: tag?.name, tag: 1, click: true, special: tag?.speciality })
                                                        doctorListAPI.get(`?search=${tag?.name}`)
                                                        setSearchType({
                                                            type: 'tag', value: {
                                                                id: tag?.speciality?.id,
                                                                nom: tag?.speciality?.specialite_nom,
                                                                parent_speciality_id: tag?.speciality?.parent_speciality_id
                                                            }
                                                        })
                                                    }}
                                                    key={index}
                                                    className="doctor-list-item">
                                                    <div className="doc-detail"
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            flex: 1
                                                        }}
                                                    >
                                                        <div className="doc-top">
                                                            <HighlightedText actualValue={tag?.name?.capitalizeEachWord?.()} searchText={searchText?.text?.trim()} />
                                                        </div>
                                                    </div>
                                                </Dropdown.Item>
                                            )
                                        })}
                                        {Array.isArray(searchRes?.categories) && searchRes?.categories?.map((category, index) => {
                                            return (
                                                <Dropdown.Item
                                                    onClick={() => {
                                                        setSearchText({ text: category?.category_nom, click: true })
                                                        doctorListAPI.get(`?search=${category?.category_nom}`)
                                                        setSearchType({
                                                            type: 'category', value: {
                                                                speciality: { id: category?.specialite_id, nom: category?.specialite_nom },
                                                                category: { id: category?.id, nom: category?.category_nom }
                                                            }
                                                        })
                                                    }}
                                                    key={index}
                                                    className="doctor-list-item">
                                                    <div className="doc-detail" style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        flex: 1
                                                    }}>
                                                        <div className="doc-top">
                                                            <HighlightedText actualValue={category?.category_nom?.capitalizeEachWord()} searchText={searchText?.text?.trim()} />
                                                        </div>
                                                    </div>
                                                </Dropdown.Item>
                                            )
                                        })}
                                        <div style={{ width: '100%', height: 1, backgroundColor: '#CCD3D8' }} />
                                        {Array.isArray(searchRes?.doctors) && searchRes?.doctors?.map((doc, index) => {
                                            const { first_name, last_name, speciality, profile_photo, ville: city, doctor_title } = doc
                                            const fullname = first_name + ' ' + last_name
                                            return (
                                                <Dropdown.Item key={index} className="doctor-list-item"
                                                    onClick={() => {
                                                        navigate(AppNav.doctorInfo, {
                                                            state: {
                                                                ...doc, isDoctor: true
                                                            }
                                                        })
                                                    }}>
                                                    <div className="doc-img">
                                                        {<img src={profile_photo || Images.defaultDoctor} alt="Doctor" />}
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'start' }} className="doc-detail">
                                                        <div style={{ display: 'flex', alignItems: 'center' }} className="doc-top">
                                                            <HighlightedText actualValue={(doctor_title ? DoctorTitle?.[doctor_title] + ' ' : '') + fullname?.capitalizeEachWord?.()} searchText={searchText?.text?.trim()} />
                                                            {speciality && <div className="doc-desc">{speciality?.specialisation?.capitalizeEachWord()}</div>}
                                                        </div>
                                                        <div className="doc-addr">
                                                            {city && city?.capitalizeEachWord()}
                                                        </div>
                                                    </div>
                                                </Dropdown.Item>
                                            )
                                        })}
                                        {Array.isArray(searchRes?.locations) && searchRes?.locations?.map((location, index) => {
                                            const { address, city, logo, location_name, organization_type } = location
                                            return (
                                                <Dropdown.Item key={index} className="doctor-list-item"
                                                    onClick={() => {
                                                        navigate(AppNav.medicalInfo, {
                                                            state: {
                                                                ...location, isDoctor: false
                                                            }
                                                        })
                                                    }}>
                                                    <div className="doc-img">
                                                        {<img src={logo || Images.defaultDoctor} alt="Doctor" />}
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'start' }} className="doc-detail">
                                                        <div style={{ display: 'flex', alignItems: 'center' }} className="doc-top">
                                                            <HighlightedText actualValue={location_name?.capitalizeEachWord?.()} searchText={searchText?.text?.trim()} />
                                                        </div>
                                                        <div className="doc-addr">
                                                            {AssociationTypes.find(assoc => assoc.id == organization_type)?.text}
                                                            {city && <br />}
                                                            {city && city?.capitalizeEachWord()}
                                                        </div>
                                                    </div>
                                                </Dropdown.Item>
                                            )
                                        })}
                                    </>
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                    </Form.Group>

                    {/* location */}
                    <div className="hsearch-btm">
                        <Form.Group className="search-box">
                            <PlacesSearchBar placeholder={i18n.t('where')} />
                            <Icon icon="ic:round-gps-fixed" />
                        </Form.Group>
                        <button type="button" className="btn btn-primary" title={i18n.t("research")} children={i18n.t("research")}
                            onClick={() => {
                                isEmpty() && onSubmit()
                            }}
                        />
                    </div>
                </div>
            </Card>
        </div>
    )
})

export const HighlightedText = ({ actualValue, searchText }) => {
    if (!actualValue || !searchText) {
        return <span className="doc-name full">{actualValue}</span>;
    }

    const cleanSearchText = ignoreAccentPunc(searchText);
    const words = cleanSearchText.split(/\s+/).filter(word => word.length > 0);
    const regex = new RegExp(`\\b(${words.join('|')})`, 'gi');
    const highlightText = (text, regex) => {
        const parts = [];
        let lastIndex = 0;
        let match;
        while ((match = regex.exec(text?.ignoreAccent?.())) !== null) {
            const startIndex = match.index;
            const endIndex = regex.lastIndex;
            if (startIndex > lastIndex) {
                parts.push(text.slice(lastIndex, startIndex));
            }

            parts.push(
                <span key={startIndex} style={{ fontWeight: 600, color: Colors.primary, textDecoration: 'underline' }}>
                    {text.slice(startIndex, endIndex)}
                </span>
            );

            lastIndex = endIndex;
        }

        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }
        return parts;
    };

    return (
        <span className="doc-name">
            {highlightText(actualValue, regex)}
        </span>
    );
};



export default FindAppointment